import styled from 'styled-components';

import {
  MatchIconAlert,
  MatchIconPositive,
  MatchIconWarning,
} from 'dashboard/components/ReportResult/MatchIcons';
import ReportResultLayout from 'dashboard/components/ReportResult/ReportResultLayout';
import HelpIcon from 'dashboard/components-lib/HelpIcon';
import TableLayout, { TableIconTD, TableTD, TableTH, TableTR } from 'dashboard/layouts/TableLayout';
import { ReportSynthetic } from 'dashboard/models/report/synthetic';
import Card from 'design-system/components/Card';
import * as Spacing from 'design-system/variables/spacing';
import Help from 'images/icons/help.svg';

const RiskAssessmentTH = styled(TableTH)`
  width: ${Spacing.BASE * 30}px;
`;

const StyledHelpIcon = styled(Help)`
  width: ${Spacing.BASE * 2}px;
  height: ${Spacing.BASE * 2}px;
  vertical-align: middle;

  path {
    transition-duration: 150ms;
    stroke: ${({ theme }) => theme.color.text.tertiary};
  }

  :hover {
    path {
      stroke: ${({ theme }) => theme.color.primary.brand};
    }
  }
`;

const getIcon = (score: number | undefined): React.ReactNode => {
  if (!score) {
    return <></>;
  }

  if (score > 500) {
    return <MatchIconAlert />;
  }

  if (score > 400) {
    return <MatchIconWarning />;
  }

  return <MatchIconPositive />;
};

interface SyntheticProps {
  report: ReportSynthetic;
  isRedacted: boolean;
  multiCardFormat: boolean;
}

const Synthetic = ({
  report,
  isRedacted: _isRedacted,
  multiCardFormat = false,
}: SyntheticProps) => {
  const {
    'sentilink-abuse-score': sentilinkAbuseScore,
    'sentilink-first-party-synthetic-score': sentilinkFirstPartySyntheticScore,
    'sentilink-third-party-synthetic-score': sentilinkThirdPartySyntheticScore,
  } = report.attributes;

  const riskAssessmentContents = (
    <>
      <Card.Title>Risk assessment</Card.Title>
      <TableLayout
        dataTest='risk-assessment-table'
        isLoading={false}
        showHeader={true}
        isEmpty={false}
        nullState={null}
        headerTR={
          <>
            <TableTH data-test='risk-assessment-table__icon-header' />
            <RiskAssessmentTH data-test='risk-assessment-table__risk-property-header'>
              Risk Property
            </RiskAssessmentTH>
            <TableTH data-test='risk-assessment-table__value-header'>Value</TableTH>
          </>
        }
      >
        <TableTR
          data-test='risk-assessment-table__row--abuse-score'
          id='risk-assessment-table__row--abuse-score'
          key='abuse-score'
        >
          <TableIconTD>{getIcon(sentilinkAbuseScore)}</TableIconTD>
          <TableTD>
            <span>Abuse Score</span>
            <HelpIcon popoverWidth={300} icon={<StyledHelpIcon />}>
              The account identity is synthetic or the identity has previously been associated with
              synthetic fraud or other first party fraud issues such as the purchase of authorized
              user tradelines.
            </HelpIcon>
          </TableTD>
          <TableTD>{sentilinkAbuseScore}/1000</TableTD>
        </TableTR>
        <TableTR
          data-test='risk-assessment-table__row--first-party-synthetic-score'
          id='risk-assessment-table__row--first-party-synthetic-score'
          key='first-party-synthetic-score'
        >
          <TableIconTD>{getIcon(sentilinkFirstPartySyntheticScore)}</TableIconTD>
          <TableTD>
            <span>First Party Synthetic Score</span>
            <HelpIcon popoverWidth={300} icon={<StyledHelpIcon />}>
              The account consists of a true name and DOB but a fictitious SSN. Commonly used when
              the applicant is trying to hide parts of their true profile.
            </HelpIcon>
          </TableTD>
          <TableTD>{sentilinkFirstPartySyntheticScore}/1000</TableTD>
        </TableTR>
        <TableTR
          data-test='risk-assessment-table__row--third-party-synthetic-score'
          id='risk-assessment-table__row--third-party-synthetic-score'
          key='third-party-synthetic-score'
        >
          <TableIconTD>{getIcon(sentilinkThirdPartySyntheticScore)}</TableIconTD>
          <TableTD>
            <span>Third Party Synthetic Score</span>
            <HelpIcon popoverWidth={300} icon={<StyledHelpIcon />}>
              The account consists of a name, DOB, and SSN that describe a fictitious person.
              Commonly used by fraudsters, often in organized crime rings, to commit financial
              crimes.
            </HelpIcon>
          </TableTD>
          <TableTD>{sentilinkThirdPartySyntheticScore}/1000</TableTD>
        </TableTR>
      </TableLayout>
    </>
  );

  return (
    <ReportResultLayout multiCardFormat={multiCardFormat}>
      {riskAssessmentContents}
    </ReportResultLayout>
  );
};

export default Synthetic;
